import { useCallback, useEffect, useRef, useState } from 'react';

const clampPercent = (value, fallback = 100) => {
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;
  return Math.max(0, Math.min(100, parsed));
};

const toMediaVolume = (value, fallback = 100) => clampPercent(value, fallback) / 100;
const isEffectivelyMuted = (value, fallback = 30) => clampPercent(value, fallback) <= 0;

const resetAudioElement = (audio) => {
  if (!audio) return;
  audio.pause();
  try {
    audio.currentTime = 0;
  } catch {
    // ignore currentTime reset failures
  }
};

const safeLoadElement = (element) => {
  if (!element || typeof element.load !== 'function') return;
  try {
    element.load();
  } catch {
    // jsdom and some mock media elements do not implement load()
  }
};

const useDualTrackPlayback = ({
  videoRef,
  audioRef,
  videoSrc,
  audioSrc,
  playbackToken = 0,
  preserveAudioOnPlaybackToken = false,
  active = true,
  shouldPlay = true,
  muted = false,
  videoVolume = 100,
  audioVolume = 30,
  reuseAudio = false,
  onMutedFallback,
  onReady,
  onPlaybackError,
}) => {
  const [state, setState] = useState('idle');
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isAutoplayFallbackMuted, setIsAutoplayFallbackMuted] = useState(false);

  const sessionRef = useRef(null);
  const lastAudioSrcRef = useRef(null);
  const audioContinuingRef = useRef(false);
  const readyNotifiedRef = useRef(false);
  const startPlaybackRef = useRef(null);
  const handledPlaybackTokenRef = useRef(playbackToken);
  const mutedRef = useRef(muted);
  const autoplayFallbackMutedRef = useRef(false);
  const onMutedFallbackRef = useRef(onMutedFallback);
  const onReadyRef = useRef(onReady);
  const onPlaybackErrorRef = useRef(onPlaybackError);

  useEffect(() => {
    mutedRef.current = muted;
  }, [muted]);

  useEffect(() => {
    onMutedFallbackRef.current = onMutedFallback;
  }, [onMutedFallback]);

  useEffect(() => {
    onReadyRef.current = onReady;
  }, [onReady]);

  useEffect(() => {
    onPlaybackErrorRef.current = onPlaybackError;
  }, [onPlaybackError]);

  const updateAutoplayFallbackMuted = useCallback((nextMuted) => {
    if (autoplayFallbackMutedRef.current === nextMuted) return;
    autoplayFallbackMutedRef.current = nextMuted;
    setIsAutoplayFallbackMuted(nextMuted);
    onMutedFallbackRef.current?.(nextMuted);
  }, []);

  const applyVolumes = useCallback(() => {
    const video = videoRef.current;
    const audio = audioRef.current;

    if (video) {
      video.volume = toMediaVolume(videoVolume, 100);
      video.muted = Boolean(muted || isAutoplayFallbackMuted);
    }

    if (audio) {
      audio.volume = toMediaVolume(audioVolume, 30);
      audio.muted = isEffectivelyMuted(audioVolume, 30);
    }
  }, [audioRef, audioVolume, isAutoplayFallbackMuted, muted, videoRef, videoVolume]);

  const pauseAll = useCallback(({ resetAudio = false } = {}) => {
    const video = videoRef.current;
    const audio = audioRef.current;

    video?.pause();
    if (audio) {
      audio.pause();
      if (resetAudio) {
        resetAudioElement(audio);
        audioContinuingRef.current = false;
      }
    }
    setState('paused');
  }, [audioRef, videoRef]);

  const stopAll = useCallback(({ resetAudio = true } = {}) => {
    sessionRef.current = null;
    const video = videoRef.current;
    const audio = audioRef.current;

    video?.pause();
    if (video) {
      try {
        video.currentTime = 0;
      } catch {
        // ignore currentTime reset failures
      }
    }

    if (audio) {
      audio.pause();
      if (resetAudio) {
        resetAudioElement(audio);
        if (audio.getAttribute('src')) {
          audio.removeAttribute('src');
          safeLoadElement(audio);
        }
        lastAudioSrcRef.current = null;
        audioContinuingRef.current = false;
      }
    }

    readyNotifiedRef.current = false;
    setIsVideoReady(false);
    setState('idle');
  }, [audioRef, videoRef]);

  const attemptPlayback = useCallback(async ({
    forceAudioRestart = false,
    preferSound = !mutedRef.current,
  } = {}) => {
    const video = videoRef.current;
    const audio = audioRef.current;
    const sessionId = sessionRef.current;
    const audioOptional = !audioSrc;

    if (!video || !videoSrc || !sessionId) return false;

    const runVideoPlay = async (allowSound) => {
      video.muted = !allowSound;
      try {
        await video.play();
        updateAutoplayFallbackMuted(!allowSound && !mutedRef.current);
        return true;
      } catch {
        return false;
      }
    };

    let videoStarted = await runVideoPlay(Boolean(preferSound));
    if (!videoStarted && preferSound) {
      videoStarted = await runVideoPlay(false);
    }

    if (sessionRef.current !== sessionId) {
      return false;
    }

    if (!videoStarted) {
      setState('error');
      onPlaybackErrorRef.current?.();
      return false;
    }

    if (!audioOptional && audio && sessionRef.current === sessionId) {
      const shouldStartAudio = forceAudioRestart || !audioContinuingRef.current;
      if (shouldStartAudio) {
        try {
          if (forceAudioRestart) {
            audio.pause();
            audio.currentTime = 0;
          }
          audio.volume = toMediaVolume(audioVolume, 30);
          audio.muted = isEffectivelyMuted(audioVolume, 30);
          await audio.play();
          audioContinuingRef.current = true;
        } catch {
          audio.pause();
          audioContinuingRef.current = false;
        }
      }
    }

    if (sessionRef.current === sessionId) {
      setState('playing');
      return true;
    }

    return false;
  }, [
    audioRef,
    audioSrc,
    audioVolume,
    updateAutoplayFallbackMuted,
    videoRef,
    videoSrc,
  ]);

  const primeAudioFromGesture = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !audioSrc) return false;
    if (audioContinuingRef.current && audio.getAttribute('src') === audioSrc) {
      return true;
    }

    if (audio.getAttribute('src') !== audioSrc) {
      audio.setAttribute('src', audioSrc);
      safeLoadElement(audio);
      lastAudioSrcRef.current = audioSrc;
    }

    audio.volume = toMediaVolume(audioVolume, 30);
    audio.muted = isEffectivelyMuted(audioVolume, 30);

    try {
      await audio.play();
      audioContinuingRef.current = true;
      return true;
    } catch {
      audio.pause();
      audioContinuingRef.current = false;
      return false;
    }
  }, [audioRef, audioSrc, audioVolume]);

  const restartLoopAlignment = useCallback(async () => {
    const video = videoRef.current;
    const audio = audioRef.current;

    if (video) {
      try {
        video.currentTime = 0;
      } catch {
        // ignore currentTime reset failures
      }
    }

    if (audio && audioSrc) {
      audio.pause();
      try {
        audio.currentTime = 0;
      } catch {
        // ignore currentTime reset failures
      }
      audioContinuingRef.current = false;
    }

    await attemptPlayback({ forceAudioRestart: true, preferSound: !mutedRef.current });
  }, [attemptPlayback, audioRef, audioSrc, audioVolume, videoRef]);

  useEffect(() => {
    applyVolumes();
  }, [applyVolumes]);

  useEffect(() => {
    startPlaybackRef.current = attemptPlayback;
  }, [attemptPlayback]);

  useEffect(() => {
    if (!active || !videoSrc) {
      pauseAll({ resetAudio: true });
      readyNotifiedRef.current = false;
      setIsVideoReady(false);
      return;
    }

    const video = videoRef.current;
    const audio = audioRef.current;
    if (!video) return;

    const forceRestartPlayback = playbackToken !== handledPlaybackTokenRef.current;
    handledPlaybackTokenRef.current = playbackToken;

    const sessionId = Symbol('dual-track-session');
    sessionRef.current = sessionId;
    readyNotifiedRef.current = false;
    setIsVideoReady(false);
    setState('preparing');
    updateAutoplayFallbackMuted(false);

    let videoCanPlay = false;
    let videoFirstFrameReady = false;
    let audioCanPlay = !audioSrc;

    const audioOptional = !audioSrc;
    const sameAudioSession = Boolean(
      reuseAudio
      && audio
      && audioSrc
      && (!forceRestartPlayback || preserveAudioOnPlaybackToken)
      && lastAudioSrcRef.current === audioSrc
      && audio.getAttribute('src') === audioSrc
    );

    const notifyReady = () => {
      if (readyNotifiedRef.current || sessionRef.current !== sessionId) return;
      readyNotifiedRef.current = true;
      onReadyRef.current?.();
    };

    const maybeStart = () => {
      if (sessionRef.current !== sessionId) return;
      if (!videoCanPlay || !videoFirstFrameReady || !(audioOptional || audioCanPlay)) return;
      setState('ready');
      notifyReady();
      if (shouldPlay && typeof startPlaybackRef.current === 'function') {
        startPlaybackRef.current({
          forceAudioRestart: !sameAudioSession,
          preferSound: !mutedRef.current,
        });
      }
    };

    const handleVideoLoadedData = () => {
      if (sessionRef.current !== sessionId) return;
      videoCanPlay = true;
      videoFirstFrameReady = true;
      setIsVideoReady(true);
      maybeStart();
    };

    const handleVideoCanPlay = () => {
      if (sessionRef.current !== sessionId) return;
      videoCanPlay = true;
      maybeStart();
    };

    const handleVideoError = () => {
      if (sessionRef.current !== sessionId) return;
      setState('error');
      onPlaybackErrorRef.current?.();
    };

    const handleAudioCanPlay = () => {
      if (sessionRef.current !== sessionId) return;
      audioCanPlay = true;
      maybeStart();
    };

    const handleAudioError = () => {
      if (sessionRef.current !== sessionId) return;
      audioCanPlay = true;
      maybeStart();
    };

    video.addEventListener('loadeddata', handleVideoLoadedData);
    video.addEventListener('canplay', handleVideoCanPlay);
    video.addEventListener('error', handleVideoError);

    if (audio && !audioOptional && !sameAudioSession) {
      audio.addEventListener('canplay', handleAudioCanPlay);
      audio.addEventListener('loadeddata', handleAudioCanPlay);
      audio.addEventListener('error', handleAudioError);
    }

    video.pause();
    if (forceRestartPlayback) {
      try {
        video.currentTime = 0;
      } catch {
        // ignore currentTime reset failures
      }
    }
    video.volume = toMediaVolume(videoVolume, 100);
    video.muted = Boolean(mutedRef.current || autoplayFallbackMutedRef.current);
    if (video.getAttribute('src') !== videoSrc) {
      // URL 变了，必须重设 src 并 load
      video.setAttribute('src', videoSrc);
      safeLoadElement(video);
    } else if (video.readyState < 2) {
      // URL 没变但缓冲被丢弃（移动端省内存），需要重新 load
      // 此时浏览器 HTTP 缓存仍然有效，load() 会从缓存读取，不会重复下载
      safeLoadElement(video);
    }
    // else: URL 没变且 readyState >= 2 → 什么都不做（最快路径，0 延迟）
    if (video.readyState >= 2) {
      handleVideoLoadedData();
    } else if (video.readyState >= 1) {
      handleVideoCanPlay();
    }

    if (audio) {
      audio.volume = toMediaVolume(audioVolume, 30);
      audio.muted = isEffectivelyMuted(audioVolume, 30);
      if (audioOptional) {
        resetAudioElement(audio);
        if (audio.getAttribute('src')) {
          audio.removeAttribute('src');
          safeLoadElement(audio);
        }
        lastAudioSrcRef.current = null;
        audioContinuingRef.current = false;
      } else if (sameAudioSession) {
        audioCanPlay = true;
        audio.volume = toMediaVolume(audioVolume, 30);
        audio.muted = isEffectivelyMuted(audioVolume, 30);
        maybeStart();
      } else {
        resetAudioElement(audio);
        if (audio.getAttribute('src') !== audioSrc) {
          audio.setAttribute('src', audioSrc);
        }
        safeLoadElement(audio);
        lastAudioSrcRef.current = audioSrc;
        if (audio.readyState >= 2) {
          handleAudioCanPlay();
        }
      }
    }

    return () => {
      video.removeEventListener('loadeddata', handleVideoLoadedData);
      video.removeEventListener('canplay', handleVideoCanPlay);
      video.removeEventListener('error', handleVideoError);
      if (audio && !audioOptional && !sameAudioSession) {
        audio.removeEventListener('canplay', handleAudioCanPlay);
        audio.removeEventListener('loadeddata', handleAudioCanPlay);
        audio.removeEventListener('error', handleAudioError);
      }
    };
  }, [
    active,
    audioRef,
    audioSrc,
    audioVolume,
    pauseAll,
    playbackToken,
    preserveAudioOnPlaybackToken,
    reuseAudio,
    shouldPlay,
    videoRef,
    videoSrc,
    videoVolume,
    updateAutoplayFallbackMuted,
  ]);

  return {
    state,
    isVideoReady,
    isAutoplayFallbackMuted,
    attemptPlayback,
    primeAudioFromGesture,
    pauseAll,
    stopAll,
    restartLoopAlignment,
    applyVolumes,
  };
};

export default useDualTrackPlayback;
